import { ApiRequest } from '../baseService'

const BaseRoute = 'app/attachment-redaction'

export interface AttachmentRedactionListFilter {
  statuses?: string[]
  fromDate?: string
  toDate?: string
  filterText?: string
  attachmentType?: string
  skipCount?: number
  maxResultCount?: number
}

/**
 * Builds the query string by hand so list params serialize as repeated keys
 * (`Statuses=Failed&Statuses=Unsupported`) — the format ASP.NET Core binds to
 * `List<T>` (axios' default `Statuses[]=` form does not).
 */
const GetList = (filter: AttachmentRedactionListFilter) => {
  const params = new URLSearchParams()

  filter.statuses?.forEach(status => params.append('Statuses', status))
  if (filter.fromDate) {
    params.append('FromDate', filter.fromDate)
  }
  if (filter.toDate) {
    params.append('ToDate', filter.toDate)
  }
  if (filter.filterText) {
    params.append('FilterText', filter.filterText)
  }
  if (filter.attachmentType) {
    params.append('AttachmentType', filter.attachmentType)
  }
  params.append('SkipCount', String(filter.skipCount ?? 0))
  params.append('MaxResultCount', String(filter.maxResultCount ?? 20))

  return ApiRequest.get(`${BaseRoute}?${params.toString()}`)
}

const Requeue = (attachmentId: string) => {
  return ApiRequest.post(`${BaseRoute}/${attachmentId}/requeue`)
}

/**
 * Delete the current redacted copy — the document reverts to Failed (hidden
 * from offshore staff) and can be re-redacted manually or re-run.
 */
const DeleteRedactedCopy = (attachmentId: string) => {
  return ApiRequest.delete(`${BaseRoute}/${attachmentId}/redacted-copy`)
}

/**
 * Withdraw whatever is published and redact from the original again. Re-queue on its own
 * refuses an already-Redacted document; this is the deliberate way to replace a copy that
 * was produced wrongly.
 */
const RedactAgain = (attachmentId: string) => {
  return ApiRequest.post(`${BaseRoute}/${attachmentId}/redact-again`)
}

export interface RedactionFeedbackSampleFilter {
  statuses?: string[]
  kind?: string
  fromDate?: string
  toDate?: string
  filterText?: string
  skipCount?: number
  maxResultCount?: number
}

/** Learning-sample curation list (same repeated-key serialization as GetList). */
const GetFeedbackSamples = (filter: RedactionFeedbackSampleFilter) => {
  const params = new URLSearchParams()

  filter.statuses?.forEach(status => params.append('Statuses', status))
  if (filter.kind) {
    params.append('Kind', filter.kind)
  }
  if (filter.fromDate) {
    params.append('FromDate', filter.fromDate)
  }
  if (filter.toDate) {
    params.append('ToDate', filter.toDate)
  }
  if (filter.filterText) {
    params.append('FilterText', filter.filterText)
  }
  params.append('SkipCount', String(filter.skipCount ?? 0))
  params.append('MaxResultCount', String(filter.maxResultCount ?? 20))

  return ApiRequest.get(`${BaseRoute}/feedback-samples?${params.toString()}`)
}

/** Approve/reject samples — only Approved samples refine the detection prompt. */
const ReviewFeedbackSamples = (ids: string[], approve: boolean) => {
  return ApiRequest.post(`${BaseRoute}/feedback-samples/review`, {
    ids,
    approve,
  })
}

/**
 * Same filter as GetFeedbackSamples, but collapsed to one row per distinct correction.
 * Counts reflect the filter — with the default Pending filter, the count is how many
 * occurrences are still waiting.
 */
const sampleParams = (
  filter: RedactionFeedbackSampleFilter,
  withPaging = true
) => {
  const params = new URLSearchParams()

  filter.statuses?.forEach(status => params.append('Statuses', status))
  if (filter.kind) {
    params.append('Kind', filter.kind)
  }
  if (filter.filterText) {
    params.append('FilterText', filter.filterText)
  }
  if (withPaging) {
    params.append('SkipCount', String(filter.skipCount ?? 0))
    params.append('MaxResultCount', String(filter.maxResultCount ?? 20))
  }

  return params.toString()
}

const GetFeedbackSampleGroups = (filter: RedactionFeedbackSampleFilter) =>
  ApiRequest.get(`${BaseRoute}/feedback-sample-groups?${sampleParams(filter)}`)

/** The same corrections arranged by the document they were captured from. */
const GetFeedbackSampleDocuments = (filter: RedactionFeedbackSampleFilter) =>
  ApiRequest.get(
    `${BaseRoute}/feedback-sample-documents?${sampleParams(filter)}`
  )

/**
 * Every correction in one document, drawn on the pages they sit on. The filter goes with
 * it so the boxes match the list the reviewer is working from — showing boxes for
 * corrections they cannot see in the list would be worse than showing none.
 */
const GetDocumentEvidence = (
  attachmentId: string,
  filter: RedactionFeedbackSampleFilter
) =>
  ApiRequest.get(
    `${BaseRoute}/feedback-sample-documents/${attachmentId}/evidence?${sampleParams(
      filter,
      false
    )}`
  )

/**
 * Decides every occurrence of the given corrections. Sends the value, not an id list:
 * a correction seen hundreds of times would otherwise ship hundreds of GUIDs each way.
 */
const ReviewFeedbackSampleGroups = (
  groups: { text: string; kind: string }[],
  approve: boolean
) => {
  return ApiRequest.post(`${BaseRoute}/feedback-sample-groups/review`, {
    groups,
    approve,
  })
}

/**
 * Every place one correction was captured. POST because the key is the corrected text —
 * PII — which must not end up in a URL, a proxy log or browser history.
 */
const GetFeedbackSampleOccurrences = (text: string, kind: string) => {
  return ApiRequest.post(`${BaseRoute}/feedback-sample-groups/occurrences`, {
    text,
    kind,
  })
}

/** The page one occurrence came from, with the correction boxed on it. */
const GetFeedbackSampleEvidence = (sampleId: string) => {
  return ApiRequest.get(`${BaseRoute}/feedback-samples/${sampleId}/evidence`)
}

const API = {
  GetList,
  Requeue,
  DeleteRedactedCopy,
  RedactAgain,
  GetFeedbackSamples,
  ReviewFeedbackSamples,
  GetFeedbackSampleGroups,
  ReviewFeedbackSampleGroups,
  GetFeedbackSampleOccurrences,
  GetFeedbackSampleEvidence,
  GetFeedbackSampleDocuments,
  GetDocumentEvidence,
}

export default API
